import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { ArrowLeft, Check, Code, Eye } from 'lucide-react';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { emailSubscriptionReducer } from '../../store/reducers/emailSubscription.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getIsSendingEmail,
  getSendEmailError,
  getSendEmailSuccess,
} from '../../store/selectors/emailSubscription.selector';
import { getAllUsers, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { User } from '../../types/user';

const { loadUsers } = userReducer.actions;
const { sendEmail, resetSendEmailStatus } = emailSubscriptionReducer.actions;

type SubscriptionType = 'NEWSLETTER' | 'PROJECT';

const EmailPreviewFrame = ({ html }: { html: string }) => {
  const ref = useRef<HTMLIFrameElement | null>(null);

  useLayoutEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;

    const resize = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;
      iframe.style.height = '0px';
      const next = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      iframe.style.height = `${next}px`;
    };

    let observer: ResizeObserver | null = null;
    const handleLoad = () => {
      resize();
      const doc = iframe.contentDocument;
      if (doc?.body && 'ResizeObserver' in window) {
        observer = new ResizeObserver(resize);
        observer.observe(doc.body);
        Array.from(doc.images).forEach((img) => {
          if (!img.complete) img.addEventListener('load', resize, { once: true });
        });
      }
    };

    iframe.addEventListener('load', handleLoad);
    handleLoad();
    return () => {
      iframe.removeEventListener('load', handleLoad);
      observer?.disconnect();
    };
  }, [html]);

  const wrapped = `<!doctype html><html><head><style>html,body{margin:0;padding:0;}</style></head><body>${
    html || '<p style="color:#999;padding:16px;">No content</p>'
  }</body></html>`;

  return (
    <iframe
      ref={ref}
      srcDoc={wrapped}
      title="Email Preview"
      style={{ width: '100%', border: 'none', display: 'block' }}
      sandbox="allow-same-origin"
    />
  );
};

export const SendEmailPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const allUsers = useAppSelector(getAllUsers);
  const isSending = useAppSelector(getIsSendingEmail);
  const sendError = useAppSelector(getSendEmailError);
  const sendSuccess = useAppSelector(getSendEmailSuccess);

  const [sendType, setSendType] = useState<SubscriptionType | ''>('');
  const [sendEmails, setSendEmails] = useState<string[]>([]);
  const [pasteEmails, setPasteEmails] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendHtmlContent, setSendHtmlContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  useEffect(() => {
    if (user && user.roles.includes('ADMIN')) {
      dispatch(loadUsers());
    }
  }, [dispatch, user]);

  useEffect(() => {
    dispatch(resetSendEmailStatus());
  }, [dispatch]);

  useEffect(() => {
    if (sendSuccess) {
      const timeout = setTimeout(() => {
        dispatch(resetSendEmailStatus());
        history.push('/email-subscriptions');
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [sendSuccess, dispatch]);

  const handleSend = () => {
    if (!sendSubject.trim() || !sendHtmlContent.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const pastedEmailsList = pasteEmails
      .split('\n')
      .map((e) => e.trim())
      .filter((e) => e !== '');

    const allEmails = [...sendEmails, ...pastedEmailsList];
    const invalidEmails = allEmails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      alert(`${t('emailSubscriptions.invalidEmails')}${invalidEmails.join(', ')}`);
      return;
    }

    if (!sendType && allEmails.length === 0) {
      alert(t('emailSubscriptions.noEmailsProvided'));
      return;
    }

    dispatch(
      sendEmail({
        subscriptionType: sendType || null,
        emails: allEmails.length > 0 ? allEmails : null,
        subject: sendSubject,
        htmlContent: sendHtmlContent,
      }),
    );
  };

  const handleBack = () => {
    history.push('/email-subscriptions');
  };

  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader
        title={t('emailSubscriptions.sendEmail')}
        subtitle={t('emailSubscriptions.sendEmailDescription')}
        action={
          <Button onClick={handleBack} startIcon={<ArrowLeft size={16} />} disabled={isSending} size="small">
            {t('emailSubscriptions.backToList', 'Back to list')}
          </Button>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('emailSubscriptions.subscriptionType')}</InputLabel>
          <Select
            value={sendType}
            label={t('emailSubscriptions.subscriptionType')}
            onChange={(e) => {
              setSendType(e.target.value as SubscriptionType | '');
              if (e.target.value) {
                setSendEmails([]);
                setPasteEmails('');
              }
            }}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          >
            <MenuItem value="">{t('emailSubscriptions.noneUseEmails')}</MenuItem>
            <MenuItem value="NEWSLETTER">{t('emailSubscriptions.newsletter')}</MenuItem>
            <MenuItem value="PROJECT">{t('emailSubscriptions.project')}</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          freeSolo
          options={allUsers.map((u: User) => u.email)}
          value={sendEmails}
          onChange={(_e, value) => {
            setSendEmails(value as string[]);
            if (value.length > 0) setSendType('');
          }}
          disabled={!!sendType}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('emailSubscriptions.emailAddresses')}
              placeholder={t('emailSubscriptions.typeOrSelectEmails')}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
            />
          )}
        />

        <TextField
          size="small"
          label={t('emailSubscriptions.pasteEmails')}
          value={pasteEmails}
          onChange={(e) => {
            setPasteEmails(e.target.value);
            if (e.target.value.trim().length > 0) setSendType('');
          }}
          disabled={!!sendType}
          multiline
          rows={3}
          placeholder="example1@email.com&#10;example2@email.com"
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
        />

        <TextField
          size="small"
          label={t('emailSubscriptions.subject')}
          value={sendSubject}
          onChange={(e) => setSendSubject(e.target.value)}
          fullWidth
          required
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } }}
        />

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('emailSubscriptions.htmlContent')}
            </Typography>
            <Button
              size="small"
              onClick={() => setShowPreview((prev) => !prev)}
              startIcon={showPreview ? <Code size={16} /> : <Eye size={16} />}
              sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
            >
              {showPreview ? t('emailSubscriptions.editor') : t('emailSubscriptions.preview')}
            </Button>
          </Box>
          {!showPreview ? (
            <TextField
              value={sendHtmlContent}
              onChange={(e) => setSendHtmlContent(e.target.value)}
              fullWidth
              required
              multiline
              placeholder="<html>...</html>"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.06)',
                  alignItems: 'flex-start',
                  maxHeight: 400,
                  overflow: 'auto',
                },
                '& textarea': { minHeight: '260px !important' },
              }}
            />
          ) : (
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.23)',
                borderRadius: 1,
                bgcolor: '#fff',
              }}
            >
              <EmailPreviewFrame html={sendHtmlContent} />
            </Box>
          )}
        </Box>

        {sendError && <Alert severity="error">{sendError}</Alert>}
        {sendSuccess && <Alert severity="success">{t('emailSubscriptions.emailsSentSuccessfully')}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={handleBack} disabled={isSending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={isSending}
            startIcon={isSending ? <CircularProgress size={20} /> : <Check size={20} />}
            sx={{
              bgcolor: '#B3EC49',
              color: 'black',
              '&:hover': { bgcolor: 'rgba(179, 236, 73, 0.8)' },
              borderRadius: '24px',
              px: 4,
            }}
          >
            {t('emailSubscriptions.send')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
